import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ZAxis,
} from "recharts";

interface BubbleChartProps {
  words: Array<{ text: string; value: number }>;
  isLoading?: boolean;
}

export function BubbleChart({ words, isLoading }: BubbleChartProps) {
  // Color palette for bubbles
  const colors = [
    "#3b82f6", // blue-500
    "#8b5cf6", // violet-500
    "#ec4899", // pink-500
    "#f59e0b", // amber-500
    "#10b981", // emerald-500
    "#6366f1", // indigo-500
  ];

  if (isLoading) {
    return (
      <Card className={cn("h-[600px]", isLoading && "animate-pulse")}>
        <CardContent className="flex h-full items-center justify-center p-6">
          <div className="h-full w-full rounded bg-gray-200" />
        </CardContent>
      </Card>
    );
  }

  if (!words || words.length === 0) {
    return (
      <Card className="h-[600px]">
        <CardContent className="flex h-full items-center justify-center p-6">
          <div className="text-center">
            <p className="text-lg text-gray-500">No messages found</p>
            <p className="text-sm text-gray-400">Try adjusting your filters</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Transform data for bubble chart
  const chartData = words.map((word, index) => ({
    x: index + 1,
    y: word.value,
    z: word.value, // Bubble size
    text: word.text,
  }));

  return (
    <Card className="h-[600px]">
      <CardContent className="h-full p-6">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              type="number"
              dataKey="x"
              name="Rank"
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              type="number"
              dataKey="y"
              name="Frequency"
              tickLine={false}
              axisLine={false}
            />
            <ZAxis
              type="number"
              dataKey="z"
              range={[100, 2000]}
              name="Frequency"
            />
            <Tooltip
              cursor={{ strokeDasharray: "3 3" }}
              content={({ active, payload }) => {
                if (!active || !payload || payload.length === 0) return null;

                const data = payload[0].payload;
                return (
                  <div className="rounded-lg border bg-white p-3 shadow-lg">
                    <p className="text-lg font-bold">{data.text}</p>
                    <p className="text-sm text-gray-600">Frequency: {data.y}</p>
                    <p className="text-xs text-gray-400">Rank: #{data.x}</p>
                  </div>
                );
              }}
            />
            <Scatter data={chartData} fill="#8884d8">
              {chartData.map((_, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={colors[index % colors.length]}
                />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
